import { IRootScopeService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { StateParams, StateService, TransitionService } from "@uirouter/angularjs";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { generateDefaultMandatoryOkrAlignmentAccountSettings } from "@gtmhub/configuration/utils/utils";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { FavoritesManager } from "@gtmhub/favorites/favorites.manager";
import { FavoritesManagerFactory } from "@gtmhub/favorites/favorites.manager.factory";
import { localize } from "@gtmhub/localization";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { OnboardingService } from "@gtmhub/onboarding";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { PlanningSessionEvents } from "@gtmhub/sessions/services/events";
import { Ng1ConfirmDeleteSessionService } from "@gtmhub/sessions/services/ng1-confirm-delete-session.service";
import { OrFiltersGroup, getTargetFilterValuesFromAllGroups, hasFiltersGroupWithValues } from "@gtmhub/shared/components/grid-filtering-bar";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { PermissionsService, getCurrentUserId } from "@gtmhub/users";
import { IPermissionsStoreState, hasPermissions } from "@gtmhub/users/redux";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { IMandatoryOkrAlignmentSettings } from "@webapp/configuration/models/configuration.model";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";
import { Session } from "@webapp/sessions/models/sessions.model";
import { MISSING_SESSION_ID_STATE_PARAM } from "../models";

export interface IGoalsRootScopeService extends IRootScopeService {
  noNavigation: boolean;
}

export interface IGoalsScope extends IModalScope, IScopeFromRedux, IExtendedRootScopeService {
  topNavBarSubtitle: string;
  currentState: string;
  shouldSeeFirstOkrGuide: boolean;
  isOkrAlignmentMandatory: boolean;
  allowedActions: string[];

  isOkrFiltersImprovementEnabled: boolean;
  areGoalsFiltered: string;
  filteredTitle: string;
  isFilteredByOriginalSession: boolean;
  isFavorite: boolean;

  openCreateDialog(): void;
  confirmDeleteSession(): void;
  addToFavorites(): void;
  removeFromFavorites(): void;
  filtersUpdated(filters: OrFiltersGroup[]): void;
  shouldHideThreeDotsMenu(): boolean;
}

interface IScopeFromRedux {
  planningSession: Session;
}

export class GoalsCtrl {
  public static $inject = [
    "$rootScope",
    "$transitions",
    "$scope",
    "$state",
    "$stateParams",
    "PlanningSessionsActions",
    "$ngRedux",
    "OnboardingService",
    "UIErrorHandlingService",
    "PermissionsService",
    "Ng1ConfirmDeleteSessionService",
    "FavoritesManagerFactory",
    "AnalyticsService",
    "AccountService",
    "FeatureTogglesFacade",
  ];

  private favoritesManager: FavoritesManager;
  private readonly planningSessionId: string;

  constructor(
    private $rootScope: IGoalsRootScopeService & IGtmhubRootScopeService,
    private $transitions: TransitionService,
    private $scope: IGoalsScope,
    private $state: StateService,
    private $stateParams: StateParams,
    private planningSessionsActions: PlanningSessionsActions,
    private $ngRedux: INgRedux,
    private onboardingService: OnboardingService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private permissionsService: PermissionsService,
    private confirmDeleteSessionService: Ng1ConfirmDeleteSessionService,
    private favoritesManagerFactory: FavoritesManagerFactory,
    private analyticsService: AnalyticsService,
    private accountService: AccountService,
    private featureToggleService: FeatureTogglesFacade,
    private sessionEvents: PlanningSessionEvents
  ) {
    this.planningSessionId = this.$stateParams.planningSessionId;

    this.favoritesManager = this.favoritesManagerFactory(["session"]);
    const favoritesSubscription = this.favoritesManager.initFavorites().subscribe(() => this.setIsFavorite(this.planningSessionId));

    this.$rootScope.noNavigation = false;

    this.$scope.topNavBarSubtitle = localize("sessions");
    this.$scope.openCreateDialog = this.openCreateDialog;
    this.$scope.confirmDeleteSession = this.confirmDeleteSession;
    this.$scope.addToFavorites = this.addToFavorites;
    this.$scope.removeFromFavorites = this.removeFromFavorites;
    this.$scope.filtersUpdated = this.filtersUpdated;
    this.$scope.shouldHideThreeDotsMenu = this.shouldHideThreeDotsMenu;

    this.$scope.currentState = this.$state.current.name;

    this.sessionEvents = new PlanningSessionEvents(this.$scope);
    const userPermissionsListener = this.sessionEvents.onSessionUpdated(() => this.getUserPermissions(this.$stateParams.planningSessionId));

    this.$scope.$on(
      "$destroy",
      this.$transitions.onSuccess({}, () => {
        this.$scope.currentState = this.$state.current.name;
      })
    );

    const removeCloseFirstOkrGuideListener = this.$rootScope.$on(
      "closeFirstOkrGuide",
      () => (this.$scope.shouldSeeFirstOkrGuide = this.onboardingService.shouldSeeFirstOkrGuide())
    );

    const unsubscribe = this.$ngRedux.connect(this.bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", () => {
      unsubscribe();
      removeCloseFirstOkrGuideListener();
      userPermissionsListener();
      favoritesSubscription.unsubscribe();
    });

    this.init();
  }

  private init = () => {
    this.featureToggleService.isFeatureAvailable(FeatureFlag.MandatoryOkrAlignmentFeature).then((isMandatoryOkrAlignmentEnabled) => {
      const mandatoryOkrAlignmentSettings: IMandatoryOkrAlignmentSettings =
        this.accountService.getAccountSetting("mandatoryOkrAlignment") || generateDefaultMandatoryOkrAlignmentAccountSettings();
      this.$scope.isOkrAlignmentMandatory =
        isMandatoryOkrAlignmentEnabled && mandatoryOkrAlignmentSettings.enabled && !mandatoryOkrAlignmentSettings.excludedUserIds.includes(getCurrentUserId());

      if (this.planningSessionId === MISSING_SESSION_ID_STATE_PARAM || !this.planningSessionId) {
        this.$scope.planningSession = null;
      } else {
        this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
        this.getUserPermissions(this.planningSessionId);
      }
      this.$scope.shouldSeeFirstOkrGuide = this.onboardingService.shouldSeeFirstOkrGuide();

      this.$scope.areGoalsFiltered = "original";
      this.$scope.isFilteredByOriginalSession = true;
      this.$scope.filteredTitle = localize("filtered_objectives");
    });
  };

  private addToFavorites = () => {
    this.favoritesManager?.addToFavorites(this.$scope.planningSession as { id: string }, "session", this.getNavItem());
    this.setIsFavorite(this.planningSessionId);
    this.analyticsService.track("New Experience Add to Favorites", { id: this.$scope.planningSession.id, type: "session", source: "detail-view" });
  };

  private removeFromFavorites = () => {
    this.favoritesManager?.removeFromFavorites(this.$scope.planningSession as { id: string }, this.getNavItem());
    this.setIsFavorite(this.planningSessionId);
    this.analyticsService.track("New Experience Remove from Favorites", { id: this.$scope.planningSession.id, type: "session", source: "detail-view" });
  };

  private getNavItem = (): INavItem => {
    return this.favoritesManager?.getFavoriteItem({ id: this.$scope.planningSession.id }) || navItemsFactory.buildSessionNavItem(this.$scope.planningSession);
  };

  private getUserPermissions(planningSessionId: string): void {
    this.permissionsService.getCurrentUserAllowedActionsPerItem(planningSessionId, "session").then(
      (actions) => {
        this.$scope.allowedActions = actions.currentUserActions;
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  }

  private bindStateToScope = (state: ISessionsStoreState): IScopeFromRedux => {
    const isPlanningSessionNonExistent = this.$stateParams.planningSessionId === MISSING_SESSION_ID_STATE_PARAM || !this.$stateParams.planningSessionId;
    const planningSession = isPlanningSessionNonExistent ? null : state.sessions.items.find((s) => s.id === this.$stateParams.planningSessionId);

    return {
      planningSession,
    };
  };

  private openCreateDialog = (): void => {
    switch (this.$state.current.name.split(".")[2]) {
      case "overview":
      case "alignmentSidebar":
        this.$state.go(`${this.$state.current.name}._goalForm.form`, { id: null });
        break;
      default:
        this.$state.go("gtmhub.goals.all._goalForm.form", { id: null });
    }
  };

  private confirmDeleteSession = () => {
    const currentSession = this.$scope.planningSession;
    this.confirmDeleteSessionService.confirmDeleteSession(currentSession).subscribe({
      next: () => {
        this.$state.go("gtmhub.sessions");
      },
      error: (error) => this.uiErrorHandlingService.handleModal(error),
    });
  };

  private filtersUpdated = (filters: OrFiltersGroup[]) => {
    // 'Objectives without session' page has no breadcrumbs title
    if (this.$stateParams.planningSessionId === MISSING_SESSION_ID_STATE_PARAM) {
      return;
    }

    // ignore empty filter groups and filter groups with empty fileds -> https://quantive-inc.atlassian.net/browse/GVS-28291
    const fullFilterGroups = filters.filter((group) => group.length > 0 && hasFiltersGroupWithValues(group));
    const allGoupsHaveSessionFilter = fullFilterGroups.length > 0 && fullFilterGroups.every((group) => group.some((filter) => filter.fieldName === "sessionId"));

    this.$scope.isFilteredByOriginalSession = allGoupsHaveSessionFilter;
    if (allGoupsHaveSessionFilter) {
      const sessionFilterValues = getTargetFilterValuesFromAllGroups(fullFilterGroups, { fieldName: "sessionId" });
      this.$scope.isFilteredByOriginalSession = sessionFilterValues.every((filterValue) => this.isFilterValueOriginalSession(filterValue));
    }

    this.$scope.areGoalsFiltered = this.$scope.isFilteredByOriginalSession ? "original" : "filtered";
  };

  private isFilterValueOriginalSession(filterValue: string[]): boolean {
    return filterValue?.length === 1 && (filterValue[0] === this.$scope.planningSession?.id || filterValue[0] === this.$stateParams.planningSessionId);
  }

  private setIsFavorite(planningSessionId): void {
    this.$scope.isFavorite = this.favoritesManager?.entityIsFavorite({ id: planningSessionId });
    this.$scope.$evalAsync();
  }

  private shouldHideThreeDotsMenu = (): boolean => {
    return (
      !hasPermissions(this.$ngRedux.getState<IPermissionsStoreState>(), "ManageSessions") ||
      !this.$scope.planningSession ||
      !this.$scope.isFilteredByOriginalSession ||
      (this.$scope.isOkrAlignmentMandatory && this.$scope.allowedActions?.indexOf("update") === -1 && this.$scope.allowedActions?.indexOf("delete") === -1)
    );
  };
}
